import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { logger } from '../utils/logger';

// Import translation files
import thCommon from './locales/th/common.json';
import thHome from './locales/th/home.json';
import thServices from './locales/th/services.json';
import thContact from './locales/th/contact.json';
import thErrors from './locales/th/errors.json';
import thAudience from './locales/th/audience.json';
import thPortfolio from './locales/th/portfolio.json';
import thPetman from './locales/th/petman.json';
import thPos from './locales/th/pos.json';
import thTradeplanner from './locales/th/tradeplanner.json';

import enCommon from './locales/en/common.json';
import enHome from './locales/en/home.json';
import enServices from './locales/en/services.json';
import enContact from './locales/en/contact.json';
import enErrors from './locales/en/errors.json';
import enAudience from './locales/en/audience.json';
import enPortfolio from './locales/en/portfolio.json';
import enPetman from './locales/en/petman.json';
import enPos from './locales/en/pos.json';
import enTradeplanner from './locales/en/tradeplanner.json';

const resources = {
  th: {
    common: thCommon,
    home: thHome,
    services: thServices,
    contact: thContact,
    errors: thErrors,
    audience: thAudience,
    portfolio: thPortfolio,
    petman: thPetman,
    pos: thPos,
    tradeplanner: thTradeplanner,
  },
  en: {
    common: enCommon,
    home: enHome,
    services: enServices,
    contact: enContact,
    errors: enErrors,
    audience: enAudience,
    portfolio: enPortfolio,
    petman: enPetman,
    pos: enPos,
    tradeplanner: enTradeplanner,
  },
};

// T043 [US2]: Implement missing translation fallback with logging
function handleMissingKey(lngs: readonly string[], _ns: string, key: string) {
  const languages = Array.isArray(lngs) ? lngs : [lngs];
  languages.forEach((lang) => {
    logger.logMissingTranslation(key, lang);
  });
  // Return the key itself as fallback
  return key;
}

// T052 [US3]: Custom storage to handle localStorage errors gracefully
class SafeLocalStorage {
  private readonly storageKey: string;

  constructor(storageKey: string) {
    this.storageKey = storageKey;
  }

  read(): string | null {
    try {
      return localStorage.getItem(this.storageKey);
    } catch (error) {
      // Handle localStorage disabled or quota exceeded
      console.warn(`[i18n] Cannot read from localStorage: ${error}`);
      return null;
    }
  }

  write(value: string): void {
    try {
      localStorage.setItem(this.storageKey, value);
    } catch (error) {
      // Handle quota exceeded or disabled storage (FR-013)
      if (error instanceof Error) {
        if (error.name === 'QuotaExceededError') {
          console.warn('[i18n] localStorage quota exceeded, language preference will not persist');
        } else {
          console.warn(`[i18n] Cannot write to localStorage: ${error.message}`);
        }
      } else {
        console.warn('[i18n] Cannot write to localStorage');
      }
      // Application continues to work, just doesn't persist language
    }
  }
}

void new SafeLocalStorage('happytech_language');

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en', // Fallback to English if Thai translation missing (FR-015)
    defaultNS: 'common',
    ns: ['common', 'home', 'services', 'contact', 'errors', 'audience', 'portfolio', 'petman', 'pos', 'tradeplanner'],
    lng: 'th', // Default to Thai

    interpolation: {
      escapeValue: false, // React already escapes
    },

    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'happytech_language',
    },

    react: {
      useSuspense: false,
    },

    // T043 [US2]: Missing translation handler (FR-016)
    saveMissing: true,
    missingKeyHandler: handleMissingKey,

    // Handle invalid language codes gracefully
    cleanCode: true,
    load: 'languageOnly',
  });

export default i18n;
